import moment from "moment";

// Occurrences of a calendar meeting, computed in the organizer's time zone.
//
// Every occurrence starts at the time of day of the series start in the zone the meeting was
// created in, so a 9:30 AM Eastern meeting stays at 9:30 AM Eastern across daylight-saving
// changes whatever the viewer's zone. Mirrors PBSCalendarAPI MeetingOccurrenceUtility (and the
// PBS Live Electron port in utils/meeting-occurrences.ts):
// - RecurringWeekday holds JS weekdays (Sunday = 0; 7 written by the mobile app is read as
//   Sunday too) in the creator's zone;
// - monthly with weekdays means the first such weekday of each month;
// - weekly intervals count weeks starting on Monday;
// - an excluded date removes the occurrence on that organizer-local date.
// Without a resolvable zone the series keeps the UTC time of day of its start (the rule the
// calendar grid's rrule expansion used), with the creator's weekdays shifted onto the UTC
// weekday of the series start and excluded dates matched by exact instant.

export interface OccurrenceEvent {
  StartTime?: string | null;
  EndTime?: string | null;
  RecurringStart?: string | null;
  RecurringUntil?: string | null;
  IsRecurring?: boolean | null;
  RecurringFreq?: number | string | null;
  RecurringInterval?: number | null;
  RecurringWeekday?: string | null;
  ExcludedDates?: string | null;
  UTCDiff?: number | string | null;
  PBSTimezone?: string | null;
  // IANA id of the organizer's zone, resolved by the Calendar API (newer API versions).
  OrganizerTimeZone?: string | null;
}

const FREQ_YEARLY = 0;
const FREQ_MONTHLY = 1;
const FREQ_WEEKLY = 2;
const FREQ_DAILY = 3;
const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

// The active PBS zones (calendar API /TimeZone, pGetTimeZones) by their Windows title, as
// stored in PBSTimezone, with the IANA id Intl needs. All eight observe DST and no two share
// an offset on any date, so a zone guessed from the creator's offset is unambiguous. Extend
// this list when PBS activates another zone.
const PBS_TIME_ZONES: ReadonlyArray<{ title: string; iana: string }> = [
  { title: "Alaskan Standard Time", iana: "America/Anchorage" },
  { title: "Atlantic Standard Time", iana: "America/Halifax" },
  { title: "Central Standard Time", iana: "America/Chicago" },
  { title: "Eastern Standard Time", iana: "America/New_York" },
  { title: "Hawaiian Standard Time", iana: "Pacific/Honolulu" },
  { title: "Mountain Standard Time", iana: "America/Denver" },
  { title: "Newfoundland Standard Time", iana: "America/St_Johns" },
  { title: "Pacific Standard Time", iana: "America/Los_Angeles" },
];

// Zones for organizers outside the active PBS zones, tried only when no active zone matches
// the creator's offset. Kept to the daylight-saving zones PBS staff work in: a zone without
// daylight saving needs no entry, because the UTC time of day already follows its wall clock.
// Mirrors ReserveTimeZones in PBSCalendarAPI CalendarUtility.
const RESERVE_TIME_ZONES: ReadonlyArray<string> = [
  "Europe/London",
  "Europe/Belgrade",
  "Europe/Athens",
  "Asia/Kolkata",
  "Asia/Manila",
  "Australia/Sydney",
  "Pacific/Auckland",
];

interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
const zoneValidity = new Map<string, boolean>();

const getFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
};

export const isKnownTimeZone = (timeZone: string): boolean => {
  const cached = zoneValidity.get(timeZone);
  if (cached !== undefined) return cached;
  let valid = false;
  try {
    getFormatter(timeZone);
    valid = true;
  } catch {
    valid = false;
  }
  zoneValidity.set(timeZone, valid);
  return valid;
};

const wallClockInZone = (ms: number, timeZone: string): WallClock => {
  const parts = getFormatter(timeZone).formatToParts(new Date(ms));
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  // Some engines print midnight as "24" even with hourCycle h23.
  const hour = read("hour");
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: hour === 24 ? 0 : hour,
    minute: read("minute"),
    second: read("second"),
  };
};

// "Floating" ms: the wall clock laid onto the UTC axis, so date arithmetic and weekday
// lookups can use the UTC getters without a zone.
const toFloatingMs = (wall: WallClock): number =>
  Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );

const floatingInZone = (ms: number, timeZone: string): number =>
  toFloatingMs(wallClockInZone(ms, timeZone));

const dayOf = (floatingMs: number): number =>
  floatingMs - (((floatingMs % DAY_MS) + DAY_MS) % DAY_MS);

const timeOfDayOf = (floatingMs: number): number =>
  floatingMs - dayOf(floatingMs);

// The instant at which the zone shows this wall clock. A wall clock inside a DST gap lands an
// hour later; an ambiguous one (fall-back hour) takes the standard-time offset, as .NET does.
const floatingToUtcMs = (floatingMs: number, timeZone: string): number => {
  const resolve = (target: number): number | null => {
    let guess = target - (floatingInZone(target, timeZone) - target);
    if (floatingInZone(guess, timeZone) !== target) {
      guess = target - (floatingInZone(guess, timeZone) - guess);
    }
    if (floatingInZone(guess, timeZone) !== target) return null;
    return floatingInZone(guess + HOUR_MS, timeZone) === target
      ? guess + HOUR_MS
      : guess;
  };
  return resolve(floatingMs) ?? resolve(floatingMs + HOUR_MS) ?? floatingMs;
};

const offsetHoursAt = (ms: number, timeZone: string): number =>
  (floatingInZone(ms, timeZone) - ms) / HOUR_MS;

const ZONE_DESIGNATOR = /(Z|[+-]\d{2}(:?\d{2})?)$/i;

// The API serializes its DateTime columns without a zone designator although they hold UTC.
// Appending "Z" makes every later parse (moment, Date) read them as UTC, whatever the viewer's
// zone. A value that already carries a designator is returned unchanged.
export const toUtcIsoString = <T>(value: T): T | string => {
  if (typeof value !== "string" || value.trim() === "") return value;
  return ZONE_DESIGNATOR.test(value) ? value : `${value}Z`;
};

// Epoch ms of a date string read as UTC (see toUtcIsoString), or null when it is not a date.
export const parseUtcMs = (value: string | undefined | null): number | null => {
  if (!value) return null;
  const ms = moment.utc(toUtcIsoString(value)).valueOf();
  return Number.isNaN(ms) ? null : ms;
};

// The creator's offset in hours, or null when the row carries none (null, undefined, "" or
// a non-numeric string): a missing offset must not read as UTC.
const parseUtcDiff = (utcDiff: OccurrenceEvent["UTCDiff"]): number | null => {
  if (utcDiff === null || utcDiff === undefined) return null;
  if (typeof utcDiff === "string" && utcDiff.trim() === "") return null;
  const value = typeof utcDiff === "number" ? utcDiff : Number(utcDiff);
  return Number.isNaN(value) ? null : value;
};

// The zone whose offset at the given instant is closest to utcDiff, within half an hour (the
// /TimeZone list truncates Newfoundland's -2.5 to -2). Null when none is that close.
const findZoneByOffset = (
  zones: ReadonlyArray<string>,
  atMs: number,
  utcDiff: number,
): string | null => {
  let best: { iana: string; distance: number } | null = null;
  for (const iana of zones) {
    if (!isKnownTimeZone(iana)) continue;
    const distance = Math.abs(offsetHoursAt(atMs, iana) - utcDiff);
    if (distance <= 0.5 && (!best || distance < best.distance)) {
      best = { iana, distance };
    }
  }
  return best?.iana ?? null;
};

// The series start: the search result carries it in RecurringStart, with StartTime holding the
// same instant (or the first occurrence) for older rows.
const seriesStartMs = (event: OccurrenceEvent): number | null =>
  parseUtcMs(event.RecurringStart) ?? parseUtcMs(event.StartTime);

// IANA zone the meeting was created in. The stored title counts only when it is an active
// PBS zone (the API derives it from the offset over the whole zone table, so other titles
// can appear); otherwise the zone matching UTCDiff at the series start, first among the
// active zones, then among the reserve zones. Null when nothing matches.
export const resolveOrganizerTimeZone = (
  event: OccurrenceEvent,
): string | null => {
  // The Calendar API resolves the zone from its zone table and returns it as OrganizerTimeZone;
  // the local lists below are only the fallback for an API or cached data without the field.
  const fromApi = event.OrganizerTimeZone?.trim();
  if (fromApi && isKnownTimeZone(fromApi)) return fromApi;

  const title = event.PBSTimezone?.trim();
  if (title) {
    const iana = PBS_TIME_ZONES.find((zone) => zone.title === title)?.iana;
    if (iana && isKnownTimeZone(iana)) return iana;
  }

  const startMs = seriesStartMs(event);
  const utcDiff = parseUtcDiff(event.UTCDiff);
  if (startMs === null || utcDiff === null) return null;

  const activeZones = PBS_TIME_ZONES.map((zone) => zone.iana);
  return (
    findZoneByOffset(activeZones, startMs, utcDiff) ??
    findZoneByOffset(RESERVE_TIME_ZONES, startMs, utcDiff)
  );
};

const parseWeekdays = (
  recurringWeekday: string | null | undefined,
  shift: number,
): Set<number> => {
  const result = new Set<number>();
  if (!recurringWeekday || typeof recurringWeekday !== "string") return result;
  for (const part of recurringWeekday.split(",")) {
    const day = parseInt(part.trim(), 10);
    // The mobile app stored Sunday as 7 (Dart numbering); the modulo below maps it to 0.
    if (!Number.isNaN(day) && day >= 0 && day <= 7) {
      result.add((((day + shift) % 7) + 7) % 7);
    }
  }
  return result;
};

const parseExcludedMs = (excludedDates: string | null | undefined): number[] => {
  if (!excludedDates) return [];
  try {
    const parsed = JSON.parse(excludedDates) as Array<{ Excluded: string }>;
    return parsed
      .map((item) => parseUtcMs(item.Excluded))
      .filter((ms): ms is number => ms !== null);
  } catch {
    return [];
  }
};

const weekStartMonday = (floatingMs: number): number => {
  const day = dayOf(floatingMs);
  const daysSinceMonday = (new Date(day).getUTCDay() + 6) % 7;
  return day - daysSinceMonday * DAY_MS;
};

const matchesPattern = (
  freq: number,
  interval: number,
  weekdays: Set<number>,
  startFloating: number,
  candidateFloating: number,
): boolean => {
  const start = new Date(startFloating);
  const candidate = new Date(candidateFloating);
  const candidateWeekday = candidate.getUTCDay();

  switch (freq) {
    case FREQ_YEARLY: {
      const yearDiff = candidate.getUTCFullYear() - start.getUTCFullYear();
      if (yearDiff % interval !== 0) return false;
      return weekdays.size > 0
        ? weekdays.has(candidateWeekday)
        : candidate.getUTCMonth() === start.getUTCMonth() &&
            candidate.getUTCDate() === start.getUTCDate();
    }
    case FREQ_MONTHLY: {
      const monthDiff =
        (candidate.getUTCFullYear() - start.getUTCFullYear()) * 12 +
        candidate.getUTCMonth() -
        start.getUTCMonth();
      if (monthDiff % interval !== 0) return false;
      return weekdays.size > 0
        ? weekdays.has(candidateWeekday) && candidate.getUTCDate() <= 7
        : candidate.getUTCDate() === start.getUTCDate();
    }
    case FREQ_DAILY: {
      const dayDiff = Math.round(
        (dayOf(candidateFloating) - dayOf(startFloating)) / DAY_MS,
      );
      if (dayDiff % interval !== 0) return false;
      return weekdays.size === 0 || weekdays.has(candidateWeekday);
    }
    default: {
      const weekDiff = Math.round(
        (weekStartMonday(candidateFloating) - weekStartMonday(startFloating)) /
          (7 * DAY_MS),
      );
      if (weekDiff % interval !== 0) return false;
      return weekdays.size > 0
        ? weekdays.has(candidateWeekday)
        : candidateWeekday === start.getUTCDay();
    }
  }
};

// Epoch ms of every occurrence start inside [fromMs, toMs], ascending. A non-recurring event
// contributes its own start when it falls in the window.
export const listOccurrenceStartsMs = (
  event: OccurrenceEvent,
  fromMs: number,
  toMs: number,
): number[] => {
  const startMs = event.IsRecurring
    ? seriesStartMs(event)
    : parseUtcMs(event.StartTime);
  if (startMs === null) return [];

  if (!event.IsRecurring) {
    return startMs >= fromMs && startMs <= toMs ? [startMs] : [];
  }

  const organizerZone = resolveOrganizerTimeZone(event);
  const timeZone = organizerZone ?? "UTC";
  // RecurringUntil carries the instant of the last occurrence as created; after a DST change
  // that occurrence can fall later in the day, so the series ends with its organizer-local day.
  const untilMs =
    parseUtcMs(event.RecurringUntil) ?? parseUtcMs(event.EndTime) ?? startMs;
  const untilDay = dayOf(floatingInZone(untilMs, timeZone));
  const startFloating = floatingInZone(startMs, timeZone);
  const startDay = dayOf(startFloating);
  const startTimeOfDay = timeOfDayOf(startFloating);

  const freqIndex = parseInt(String(event.RecurringFreq), 10);
  const freq =
    freqIndex >= FREQ_YEARLY && freqIndex <= FREQ_DAILY
      ? freqIndex
      : FREQ_WEEKLY;
  const interval = Math.max(event.RecurringInterval || 1, 1);

  // Without a zone the stored weekdays (creator's zone, by UTCDiff) move onto the UTC
  // weekday of the series start, as the calendar grid does.
  const utcDiff = parseUtcDiff(event.UTCDiff) ?? 0;
  const weekdayShift = organizerZone
    ? 0
    : new Date(startMs).getUTCDay() -
      new Date(startMs + utcDiff * HOUR_MS).getUTCDay();
  const weekdays = parseWeekdays(event.RecurringWeekday, weekdayShift);

  const excludedMs = parseExcludedMs(event.ExcludedDates);
  const excludedDays = new Set(
    excludedMs.map((ms) => dayOf(floatingInZone(ms, timeZone))),
  );
  const excludedInstants = new Set(excludedMs.map((ms) => ms - (ms % 1000)));

  // Organizer-local days to try: the fetched window widened by a day on each side (a zone
  // conversion can move an occurrence across a day boundary), clipped to the series.
  const firstDay = Math.max(
    dayOf(floatingInZone(fromMs, timeZone)) - DAY_MS,
    startDay,
  );
  const lastDay = Math.min(
    dayOf(floatingInZone(toMs, timeZone)) + DAY_MS,
    untilDay,
  );

  const result: number[] = [];
  for (let day = firstDay; day <= lastDay; day += DAY_MS) {
    const candidateFloating = day + startTimeOfDay;

    // The cheap checks run on the floating candidate so the zone conversion (Intl) is paid
    // only for days that match the pattern.
    if (
      !matchesPattern(freq, interval, weekdays, startFloating, candidateFloating)
    )
      continue;
    if (organizerZone && excludedDays.has(day)) continue;

    const candidateMs = floatingToUtcMs(candidateFloating, timeZone);
    if (candidateMs < fromMs || candidateMs > toMs) continue;
    if (candidateMs < startMs || day > untilDay) continue;
    if (
      !organizerZone &&
      excludedInstants.has(candidateMs - (candidateMs % 1000))
    )
      continue;

    result.push(candidateMs);
  }

  return result;
};
